import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../../components/Skeleton.js";
import { Link } from "react-router-dom";
import { api, ApiError } from "../../lib/api.js";
import { useTableState } from "../../lib/useTableState.js";
import TableToolbar from "../../components/TableToolbar.js";

type SecurityCenterData = Awaited<ReturnType<typeof api.securityCenter>>;
type ConnectionsData = Awaited<ReturnType<typeof api.platformConnections>>;
type AssignmentsData = Awaited<ReturnType<typeof api.userRoleAssignments>>;
type WorkspaceStatus = SecurityCenterData["workspaces"][number];
type AuditEvent = SecurityCenterData["recentEvents"][number];
type ConnectionRow = ConnectionsData["connections"][number];
type Assignment = AssignmentsData["assignments"][number];

export default function AdminSecurityCenter() {
  const [data, setData] = useState<SecurityCenterData | null>(null);
  const [connections, setConnections] = useState<ConnectionsData | null>(null);
  const [privileged, setPrivileged] = useState<AssignmentsData["assignments"]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const reload = async () => {
    const [d, c, a] = await Promise.all([
      api.securityCenter(),
      api.platformConnections().catch(() => null as ConnectionsData | null),
      api.userRoleAssignments({ status: "active" }).catch(() => ({ assignments: [] } as AssignmentsData))
    ]);
    setData(d);
    setConnections(c);
    setPrivileged(a.assignments.filter((x) => x.scope === "platform" || x.roleKey.includes("owner")));
  };

  useEffect(() => {
    let cancelled = false;
    reload()
      .catch((e) => { if (!cancelled) setErr(e instanceof ApiError ? e.message : String(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const onRevoke = async (assignmentId: string, label: string) => {
    if (!window.confirm(`Revoke ${label}?`)) return;
    setBusyId(assignmentId);
    setErr(null);
    try {
      await api.revokeUserRole(assignmentId);
      setBanner(`Revoked ${label}.`);
      await reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  const onRestore = async (workspaceId: string, slug: string) => {
    if (!window.confirm(`Restore (unsuspend) workspace "${slug}"?`)) return;
    setBusyId(workspaceId);
    setErr(null);
    try {
      await api.restoreTenant(workspaceId);
      setBanner(`Restored workspace ${slug}.`);
      await reload();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  const missingSecretConnections = useMemo(
    () => connections ? connections.connections.filter((c) => c.enabled && !c.secretConfigured) : [],
    [connections]
  );
  const suspendedWorkspaces = data?.workspaces.filter((w) => w.status === "suspended") ?? [];

  const recentEventsReversed = useMemo(
    () => (data?.recentEvents ?? []).slice().reverse(),
    [data]
  );

  const tPriv = useTableState(privileged as unknown as Array<Record<string, unknown>>, {
    searchFields: ["userId", "roleKey", "scope", "workspaceId"],
    initialPageSize: 25
  });
  const tMissing = useTableState(missingSecretConnections as unknown as Array<Record<string, unknown>>, {
    searchFields: ["environment", "workspaceName", "workspaceId", "baseUrl"],
    initialPageSize: 25
  });
  const tWorkspaces = useTableState((data?.workspaces ?? []) as unknown as Array<Record<string, unknown>>, {
    searchFields: ["slug", "status"],
    initialPageSize: 25
  });
  const tEvents = useTableState(recentEventsReversed as unknown as Array<Record<string, unknown>>, {
    searchFields: ["action", "actorRole", "actorUserId", "workspaceId"],
    initialPageSize: 25
  });

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Security center</h1>
          <p>Authentication health, rate-limit counters, workspace suspensions and recent platform audit events.</p>
        </div>
      </header>

      {err && <div className="alert alert-error">{err}</div>}
      {banner && <div className="alert alert-ok">{banner}</div>}
      {loading && <SkeletonTable rows={5} columns={4} />}

      {data && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-head"><strong>Posture overview</strong></div>
            <div className="card-body" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
              <div>
                <div className="soft">Auth failures</div>
                <div style={{ fontSize: 22 }}>{data.metrics.authFailures}</div>
              </div>
              <div>
                <div className="soft">Rate-limited</div>
                <div style={{ fontSize: 22 }}>{data.metrics.rateLimited}</div>
              </div>
              <div>
                <div className="soft">Secrets missing</div>
                <div style={{ fontSize: 22, color: missingSecretConnections.length > 0 ? "#b91c1c" : undefined }}>
                  {missingSecretConnections.length}
                </div>
              </div>
              <div>
                <div className="soft">Suspended tenants</div>
                <div style={{ fontSize: 22, color: suspendedWorkspaces.length > 0 ? "#b45309" : undefined }}>
                  {suspendedWorkspaces.length}
                </div>
              </div>
            </div>
          </div>

          {privileged.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-head"><strong>Privileged access ({privileged.length})</strong></div>
              <div className="card-body">
                <TableToolbar
                  search={tPriv.search} setSearch={tPriv.setSearch}
                  page={tPriv.page} setPage={tPriv.setPage}
                  pageSize={tPriv.pageSize} setPageSize={tPriv.setPageSize}
                  totalPages={tPriv.totalPages} totalRows={tPriv.totalRows}
                  hideExport
                  searchPlaceholder="Search user / role / workspace…"
                />
                <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr><th>User</th><th>Role</th><th>Scope</th><th>Granted</th><th>Expires</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {(tPriv.paged as unknown as Assignment[]).map((a) => (
                      <tr key={a.id}>
                        <td><code>{a.userId}</code></td>
                        <td><span className="pill pill-warning">{a.roleKey}</span></td>
                        <td className="soft">{a.scope}{a.workspaceId ? ` · ${a.workspaceId}` : ""}</td>
                        <td className="soft">{new Date(a.assignedAt).toLocaleDateString()}</td>
                        <td className="soft">{a.expiresAt ? new Date(a.expiresAt).toLocaleDateString() : "—"}</td>
                        <td>
                          <button
                            className="btn btn-sm"
                            type="button"
                            disabled={busyId === a.id}
                            onClick={() => void onRevoke(a.id, `${a.roleKey} for ${a.userId}`)}
                            style={{ color: "#b91c1c" }}
                          >
                            {busyId === a.id ? "Working…" : "Revoke"}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          )}

          {missingSecretConnections.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <div className="card-head"><strong>Connections needing rotation</strong></div>
              <div className="card-body">
                <TableToolbar
                  search={tMissing.search} setSearch={tMissing.setSearch}
                  page={tMissing.page} setPage={tMissing.setPage}
                  pageSize={tMissing.pageSize} setPageSize={tMissing.setPageSize}
                  totalPages={tMissing.totalPages} totalRows={tMissing.totalRows}
                  hideExport
                  searchPlaceholder="Search environment / workspace…"
                />
                <div className="table-scroll">
                <table className="data-table">
                  <thead>
                    <tr><th>Environment</th><th>Workspace</th><th>Base URL</th><th>Actions</th></tr>
                  </thead>
                  <tbody>
                    {(tMissing.paged as unknown as ConnectionRow[]).map((c) => (
                      <tr key={c.environment}>
                        <td><code>{c.environment}</code></td>
                        <td>
                          {c.workspaceId ? (
                            <Link to={`/admin/workspaces/${encodeURIComponent(c.workspaceId)}`}>
                              {c.workspaceName ?? c.workspaceId}
                            </Link>
                          ) : "—"}
                        </td>
                        <td className="soft">{c.baseUrl}</td>
                        <td>
                          <Link className="btn btn-sm" to="/admin/instances">Rotate in Instances</Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
              </div>
            </div>
          )}

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-head"><strong>Workspace status</strong></div>
            <div className="card-body">
              <TableToolbar
                search={tWorkspaces.search} setSearch={tWorkspaces.setSearch}
                page={tWorkspaces.page} setPage={tWorkspaces.setPage}
                pageSize={tWorkspaces.pageSize} setPageSize={tWorkspaces.setPageSize}
                totalPages={tWorkspaces.totalPages} totalRows={tWorkspaces.totalRows}
                hideExport
                searchPlaceholder="Search slug / status…"
              />
              <table className="data-table">
                <thead>
                  <tr><th>Workspace</th><th>Status</th><th>Suspended at</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {(tWorkspaces.paged as unknown as WorkspaceStatus[]).map((w) => (
                    <tr key={w.id}>
                      <td><code>{w.slug}</code></td>
                      <td>
                        <span className={`pill ${w.status === "suspended" ? "pill-warn" : "pill-muted"}`}>
                          {w.status}
                        </span>
                      </td>
                      <td className="soft">{w.suspendedAt ? new Date(w.suspendedAt).toLocaleString() : "—"}</td>
                      <td>
                        <div style={{ display: "flex", gap: 6 }}>
                          <Link className="btn btn-sm" to={`/admin/workspaces/${encodeURIComponent(w.id)}`}>Open</Link>
                          {w.status === "suspended" && (
                            <button
                              className="btn btn-sm"
                              type="button"
                              disabled={busyId === w.id}
                              onClick={() => void onRestore(w.id, w.slug)}
                            >
                              {busyId === w.id ? "Working…" : "Restore"}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="card-head"><strong>Recent audit events</strong></div>
            <div className="card-body">
              <TableToolbar
                search={tEvents.search} setSearch={tEvents.setSearch}
                page={tEvents.page} setPage={tEvents.setPage}
                pageSize={tEvents.pageSize} setPageSize={tEvents.setPageSize}
                totalPages={tEvents.totalPages} totalRows={tEvents.totalRows}
                hideExport
                searchPlaceholder="Search action / actor / workspace…"
              />
              <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr><th>When</th><th>Action</th><th>Actor</th><th>Workspace</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {recentEventsReversed.length === 0 ? (
                    <tr><td colSpan={5} className="soft">No audit entries.</td></tr>
                  ) : (tEvents.paged as unknown as AuditEvent[]).map((e) => (
                    <tr key={e.id}>
                      <td className="soft">{new Date(e.occurredAt).toLocaleString()}</td>
                      <td><code>{e.action}</code></td>
                      <td className="soft">{e.actorRole}{e.actorUserId ? ` · ${e.actorUserId}` : ""}</td>
                      <td className="soft">{e.workspaceId ? <code>{e.workspaceId}</code> : "—"}</td>
                      <td>
                        <Link
                          className="btn btn-sm"
                          to={e.workspaceId
                            ? `/admin/audit?workspaceId=${encodeURIComponent(e.workspaceId)}&action=${encodeURIComponent(e.action)}`
                            : `/admin/audit?action=${encodeURIComponent(e.action)}`}
                        >
                          Audit ↗
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
